// Vendor portal guided product tour registry.
// Each tour is a list of steps targeting `data-tour="…"` selectors.
// See docs/vendor-portal-tours.md for the full guide.

import {
  LayoutDashboard,
  ClipboardList,
  Users,
  Building2,
  FileText,
  DollarSign,
  Bell,
  UserCog,
  UserCircle,
  type LucideIcon,
} from "lucide-react";

export type TourPlacement = "top" | "bottom" | "left" | "right" | "auto";

export interface TourStep {
  /** CSS selector — usually a `[data-tour="…"]` attribute on the target. */
  target: string;
  title: string;
  body: string;
  /** Optional YouTube embed URL (full URL, the engine renders a 16:9 iframe). */
  videoUrl?: string;
  /** Preferred placement. The engine still falls back to other sides when there is no room. */
  placement?: TourPlacement;
}

export interface TourConfig {
  id: string;
  /** Display name for the Guidebook hub card. Falls back to the id when missing. */
  name?: string;
  /** Short one-liner shown on the Guidebook hub card. */
  summary?: string;
  /** Lucide icon rendered on the Guidebook hub card. */
  icon?: LucideIcon;
  /**
   * The vendor-portal tab value that must be active for this tour's step
   * targets to be mounted. When set, starting this tour from the Guidebook
   * while on a different tab will switch to this tab first before activating
   * the spotlight. Leave undefined for tab-agnostic tours (e.g. the welcome
   * dashboard tour whose targets live in the persistent header/sidebar).
   */
  requiredTab?: string;
  steps: TourStep[];
}

export const dashboardTour: TourConfig = {
  id: "dashboard",
  name: "Vendor Portal welcome tour",
  summary: "The big-picture walk-through of every tab in the portal — start here if you're brand new.",
  icon: LayoutDashboard,
  steps: [
    {
      target: '[data-tour="dashboard-brand"]',
      title: "Welcome to the Vendor Portal",
      body: "This is your home base for everything coming from your hosts — tasks, payouts, forms, and chat. We'll show you around in a minute.",
      placement: "bottom",
    },
    {
      target: '[data-tour="dashboard-stats"]',
      title: "Your stats at a glance",
      body: "Active tasks waiting on you, total earnings to date, and your aggregate rating from hosts. These update in real time.",
      placement: "bottom",
    },
    {
      target: '[data-tour="dashboard-tab-tasks"]',
      title: "Tasks",
      body: "All work assigned to you lives here. Tap a task to see details, message the host, upload photos, and mark it done.",
      placement: "bottom",
    },
    {
      target: '[data-tour="dashboard-tab-hosts"]',
      title: "Hosts",
      body: "The hosts you work with, with quick links to past jobs and open work.",
      placement: "bottom",
    },
    {
      target: '[data-tour="dashboard-tab-payments"]',
      title: "Pay",
      body: "Connect your payout account and track every payment from each host.",
      placement: "bottom",
    },
    {
      target: '[data-tour="dashboard-tab-notifications"]',
      title: "Alerts",
      body: "Money events, new task assignments, and other in-app notifications land here. The bell at the top right shows the unread count.",
      placement: "bottom",
    },
    {
      target: '[data-tour="dashboard-tab-team"]',
      title: "Team",
      body: "Owners can invite teammates, give each member their own login, and decide who sees what. Skip this if you work solo.",
      placement: "top",
    },
    {
      target: '[data-tour="dashboard-tab-profile"]',
      title: "Profile",
      body: "Edit your business info, manage notification preferences, and opt in to the public Vendor Marketplace so new hosts can find you.",
      placement: "top",
    },
    {
      target: '[data-tour="dashboard-guide-button"]',
      title: "Re-open the Guidebook anytime",
      body: "The Guidebook button up here is your single home for every guided tour in the portal. Open it any time to replay this welcome tour or jump into a tab-specific walkthrough.",
      placement: "bottom",
    },
  ],
};

export const tasksTour: TourConfig = {
  id: "tasks",
  name: "Tasks",
  summary: "How to accept jobs, chat with hosts, upload before/after photos, and mark work complete.",
  icon: ClipboardList,
  requiredTab: "tasks",
  steps: [
    {
      target: '[data-tour="dashboard-tab-tasks"]',
      title: "The Tasks tab",
      body: "Every job a host assigns you shows up here. Use this tour to learn how to accept work, upload before/after photos, and mark a task complete.",
      placement: "bottom",
    },
    {
      target: '[data-tour="tasks-tab-switcher"]',
      title: "Active vs Completed",
      body: "Active is your current to-do list, Forms collects every form a host has sent you to fill out, and Completed is your history — useful when a host asks you to look up a past job.",
      placement: "bottom",
    },
    {
      target: '[data-tour="tasks-active-list"]',
      title: "Your active tasks",
      body: "Each card shows the job title, the property, the scheduled date, and the price. Newest assignments float to the top.",
      placement: "top",
    },
    {
      target: '[data-tour="tasks-task-card"]',
      title: "Open a task to do real work",
      body: "Tap any card to see the full description, chat with the host, attach before/after photos, and mark the job complete or accept it. Photos are uploaded right inside the task chat — just hit the paperclip.",
      placement: "top",
    },
    {
      target: '[data-tour="dashboard-guide-button"]',
      title: "Replay anytime",
      body: "Hit the Guidebook button whenever you want a refresher — this tour will be marked 'For this page' while you're on Tasks.",
      placement: "bottom",
    },
  ],
};

// Forms-on-tasks — the standalone Forms tab (and its tour) was retired; form-tasks
// now live in the Tasks feed's Forms sub-tab, covered by the tasks tour.

export const hostsTour: TourConfig = {
  id: "hosts",
  name: "Hosts",
  summary: "Browse every host you work with, see their full job history, and spot open work fast.",
  icon: Users,
  requiredTab: "hosts",
  steps: [
    {
      target: '[data-tour="dashboard-tab-hosts"]',
      title: "Your Hosts CRM",
      body: "Every host you've worked with shows up here as a client card — even if you only have a single task with them. Use this tour to learn how to dig into a host's history and find their open work fast.",
      placement: "bottom",
    },
    {
      target: '[data-tour="hosts-list"]',
      title: "All your host clients",
      body: "Hosts are sorted by most recent activity. Each card shows the host's name, business, total jobs you've done together, and when their last job was — handy when a returning host messages you out of the blue.",
      placement: "top",
    },
    {
      target: '[data-tour="hosts-host-card"]',
      title: "Tap a host to see everything",
      body: "Opening a host card shows their full task history with you, the properties they've shared, and lets owners assign a default team member per property so the right person picks up new jobs automatically.",
      placement: "top",
    },
    {
      target: '[data-tour="hosts-open-tasks-link"]',
      title: "Spot open work at a glance",
      body: "When a host has unfinished tasks, the open count lights up in blue. Tap into the host to see exactly what's still on your plate for them — no need to dig through the Tasks tab.",
      placement: "top",
    },
    {
      target: '[data-tour="dashboard-guide-button"]',
      title: "Replay anytime",
      body: "Hit the Guidebook button whenever you want a refresher — this tour will be marked 'For this page' while you're on the Hosts tab.",
      placement: "bottom",
    },
  ],
};

// Auto-starts the first time a vendor opens any host card. Called out
// separately from the list-level `hosts` tour because the per-property
// "Auto-assign" dropdown and the per-host task history live on a sub-view
// that owners specifically tend not to discover on their own.
export const hostsDetailTour: TourConfig = {
  id: "hostsDetail",
  name: "Inside a host",
  summary: "Drill into one host: set per-property auto-assignments and review your full job history together.",
  icon: Building2,
  requiredTab: "hosts",
  steps: [
    {
      target: '[data-tour="hosts-detail-properties"]',
      title: "Properties & per-property assignments",
      body: "Every property this host has shared with you is listed here. Owners get an Auto-assign dropdown next to each one — pick a teammate and any future task at that address routes straight to them, no manual triage needed.",
      placement: "top",
    },
    {
      target: '[data-tour="hosts-detail-task-history"]',
      title: "Full task history with this host",
      body: "Every job you've done for this host — open or completed — lives here in one scroll. Handy when a returning host references an old request, or when you want to see how often you've worked together.",
      placement: "top",
    },
  ],
};

export const paymentsTour: TourConfig = {
  id: "payments",
  name: "Pay",
  summary: "Connect Stripe for direct payouts, track every payment, and pull money out instantly.",
  icon: DollarSign,
  requiredTab: "payments",
  steps: [
    {
      target: '[data-tour="dashboard-tab-payments"]',
      title: "Getting paid",
      body: "Everything money-related lives here — connecting your bank, tracking what you've earned per host, and pulling money out instantly when you need it.",
      placement: "bottom",
    },
    {
      target: '[data-tour="payments-connect-cta"]',
      title: "Connect Stripe to receive direct payouts",
      body: "Click Connect to set up direct bank payouts via Stripe. It's a quick KYC flow (ID + bank account). You only need to do this once per vendor account, and it unlocks instant payouts.",
      placement: "bottom",
    },
    {
      target: '[data-tour="payments-payout-speed"]',
      title: "Standard or Instant",
      body: "Choose your default speed. Standard is free and arrives in 1–2 business days. Instant lands in minutes for a 1% fee. You can always override the choice on a single payment.",
      placement: "top",
    },
    {
      target: '[data-tour="payments-history-list"]',
      title: "Your payment history",
      body: "Payments are grouped by month with running totals — gross, fees, and your net. Useful for invoicing and bookkeeping at the end of the month.",
      placement: "top",
    },
    {
      target: '[data-tour="payments-history-row"]',
      title: "Each payment, in detail",
      body: "Every row shows the linked task, the amount, any platform fees, and the status. If a payment is settled and you've connected Stripe, hit Pay out instantly to send the net amount to your bank in minutes.",
      placement: "top",
    },
    {
      target: '[data-tour="dashboard-guide-button"]',
      title: "Replay anytime",
      body: "Hit the Guidebook button whenever you want a refresher — this tour will be marked 'For this page' while you're on the Pay tab.",
      placement: "bottom",
    },
  ],
};

export const notificationsTour: TourConfig = {
  id: "notifications",
  name: "Alerts",
  summary: "Filter and triage in-app alerts so payouts, disputes, and assignments never slip through.",
  icon: Bell,
  requiredTab: "notifications",
  steps: [
    {
      target: '[data-tour="dashboard-tab-notifications"]',
      title: "Alerts",
      body: "Payout updates, dispute notices, and other in-app alerts land here. The bell at the top right shows your unread count even when you're on a different tab.",
      placement: "bottom",
    },
    {
      target: '[data-tour="notifications-filters"]',
      title: "Filter by category and status",
      body: "Narrow the list down to just payouts, just disputes, or anything else. Combine with the read/unread toggle to dig out the alert you're looking for fast.",
      placement: "bottom",
    },
    {
      target: '[data-tour="notifications-list"]',
      title: "Each alert in detail",
      body: "Every card shows a coloured category badge so you can spot the important stuff at a glance. Tap Open to jump straight to the related task or payout, or Mark read to clear the unread badge.",
      placement: "top",
    },
    {
      target: '[data-tour="dashboard-guide-button"]',
      title: "Replay anytime",
      body: "Hit the Guidebook button whenever you want a refresher — this tour will be marked 'For this page' while you're on the Alerts tab.",
      placement: "bottom",
    },
  ],
};

export const teamTour: TourConfig = {
  id: "team",
  name: "Team (owner-only)",
  summary: "Invite teammates with their own logins, set roles, and hand off ownership when needed.",
  icon: UserCog,
  requiredTab: "team",
  steps: [
    {
      target: '[data-tour="dashboard-tab-team"]',
      title: "Your team",
      body: "Invite teammates, give each one their own login, and decide who can do what. Each member gets their own task assignments and notifications.",
      placement: "bottom",
    },
    {
      target: '[data-tour="team-add-member"]',
      title: "Invite a teammate",
      body: "Add their name, phone, and (optionally) email. They'll get an SMS invite to set up their own login — no shared passwords.",
      placement: "bottom",
    },
    {
      target: '[data-tour="team-members-list"]',
      title: "Roles and permissions",
      body: "Members handle the work assigned to them. Admins can also manage host property assignments. The Owner badge belongs to whoever set the account up — only one owner at a time.",
      placement: "top",
    },
    {
      target: '[data-tour="team-transfer-ownership"]',
      title: "Hand off ownership",
      body: "Need to pass the account to someone else? Transfer ownership and the new owner inherits all owner-only privileges. You'll keep working as a regular admin.",
      placement: "top",
    },
    {
      target: '[data-tour="dashboard-guide-button"]',
      title: "Replay anytime",
      body: "Hit the Guidebook button whenever you want a refresher — this tour will be marked 'For this page' while you're on the Team tab.",
      placement: "bottom",
    },
  ],
};

export const profileTour: TourConfig = {
  id: "profile",
  name: "Profile",
  summary: "Update business info, set notification preferences, and join the public Vendor Marketplace.",
  icon: UserCircle,
  requiredTab: "profile",
  steps: [
    {
      target: '[data-tour="dashboard-tab-profile"]',
      title: "Your profile",
      body: "Manage your personal contact info, your business details, and how you want to be notified about new work.",
      placement: "bottom",
    },
    {
      target: '[data-tour="profile-personal-contact"]',
      title: "Personal contact info",
      body: "Your phone number is what you'll use to log in via OTP, so keep it current. Email is optional but handy for important alerts and password-free magic links.",
      placement: "top",
    },
    {
      target: '[data-tour="profile-marketplace-listing"]',
      title: "Join the public Vendor Marketplace",
      body: "Owners can opt in here to be listed in the public PadPulse Vendor Marketplace ($99/year). Hosts browsing the marketplace will see your name, services, ratings, bio, and photo. Toggle off anytime if you only want to keep working with your existing hosts.",
      placement: "top",
    },
    {
      target: '[data-tour="profile-notification-preferences"]',
      title: "Notification preferences",
      body: "Pick which events ping you on SMS vs email — task assignments, new task messages, and form chats. SMS is on by default for assignments so you never miss a job.",
      placement: "top",
    },
    {
      target: '[data-tour="dashboard-guide-button"]',
      title: "Replay anytime",
      body: "Hit the Guidebook button whenever you want a refresher — this tour will be marked 'For this page' while you're on the Profile tab.",
      placement: "bottom",
    },
  ],
};

export const TOUR_REGISTRY: Record<string, TourConfig> = {
  [dashboardTour.id]: dashboardTour,
  [tasksTour.id]: tasksTour,
  [hostsTour.id]: hostsTour,
  [hostsDetailTour.id]: hostsDetailTour,
  [paymentsTour.id]: paymentsTour,
  [notificationsTour.id]: notificationsTour,
  [teamTour.id]: teamTour,
  [profileTour.id]: profileTour,
};

// Ordered list of tours surfaced in the Guidebook hub popup. Welcome tour
// first, then per-tab tours in the same left-to-right order as the dashboard
// tab list. Team is owner-only — hide it for non-owners.
export const TOUR_HUB_ORDER: TourConfig[] = [
  dashboardTour,
  tasksTour,
  hostsTour,
  hostsDetailTour,
  paymentsTour,
  notificationsTour,
  teamTour,
  profileTour,
];

// Map a vendor-portal tab value to the tourId surfaced by the Guide button.
// Returns null for tabs without a registered tour (the Guide button hides
// itself in that case). Each tab gets its own focused tour; the dashboard
// tour is reserved for the first-time welcome experience (see
// `autoStartTourId` below) and is not surfaced again from the Guide button
// once the vendor has seen it. Add cases here as per-tab tours are built.
//
// `subView` lets a tab swap to a sub-view-specific tour when the vendor has
// drilled into a nested screen. Today only the Hosts tab uses this — when a
// host card is open, the Guide button replays the per-host detail walk-through
// instead of the list-level tour.
export function tourIdForTab(tabValue: string, subView?: string | null): string | null {
  switch (tabValue) {
    case "tasks":
      return tasksTour.id;
    case "hosts":
      return subView === "hostDetail" ? hostsDetailTour.id : hostsTour.id;
    case "payments":
      return paymentsTour.id;
    case "notifications":
      return notificationsTour.id;
    case "team":
      return teamTour.id;
    case "profile":
      return profileTour.id;
    default:
      return null;
  }
}

// TourId to auto-start on first portal load when no record exists. Returns
// null when the active tab has no auto-start tour, so deep-links to tabs
// without a tour do not pop a tour out of context.
//
// Tasks is the default landing tab for new vendors, so first-timers there
// see the full `dashboard` welcome tour that walks them through the whole
// portal shell. Hosts, Forms and Payments auto-start their own tab-specific
// tours the first time someone deep-links into them, then never again.
export function autoStartTourId(activeTab: string): string | null {
  switch (activeTab) {
    case "tasks":
      return dashboardTour.id;
    case "hosts":
      return hostsTour.id;
    case "payments":
      return paymentsTour.id;
    case "notifications":
      return notificationsTour.id;
    case "team":
      return teamTour.id;
    case "profile":
      return profileTour.id;
    default:
      return null;
  }
}
